import { utils, providers } from 'ethers';
import { getAccount } from '@wagmi/core';
import { getWagmiConfig } from '../../../../lib/web3/wagmi-config';
import { CURATED_TOKENS } from './tokens';
import { CHAIN_ID_ETHEREUM, CHAIN_ID_SEPOLIA, CHAIN_ID_ZCHAIN, CHAIN_ID_ZEPHYR, ZERO_ADDRESS } from './constants';

// Re-export constants for backward compatibility
// TODO: Update imports across codebase to import directly from './constants' instead of './utils'
export { CHAIN_ID_ETHEREUM, CHAIN_ID_SEPOLIA, CHAIN_ID_ZCHAIN, CHAIN_ID_ZEPHYR, ZERO_ADDRESS };

export interface BridgeParams {
  amount: string;
  fromChainId: number;
  toChainId: number;
  fromWalletAddress?: string;
  toWalletAddress?: string;
  fromToken?: {
    symbol: string;
    name: string;
    tokenAddress: string;
    logoUrl?: string;
    decimals?: number;
  };
  toToken?: {
    symbol: string;
    name: string;
    tokenAddress: string;
    logoUrl?: string;
    decimals?: number;
  };
}

export const L1_CHAIN_IDS = [CHAIN_ID_ETHEREUM, CHAIN_ID_SEPOLIA] as const;
export const L2_CHAIN_IDS = [CHAIN_ID_ZCHAIN, CHAIN_ID_ZEPHYR] as const;

// Chain names for UI display
export const CHAIN_NAMES: Record<number, string> = {
  [CHAIN_ID_ETHEREUM]: 'Ethereum',
  [CHAIN_ID_SEPOLIA]: 'Sepolia',
  [CHAIN_ID_ZCHAIN]: 'Z Chain',
  [CHAIN_ID_ZEPHYR]: 'Zephyr',
};

// Reverse mapping with aliases to handle both frontend display names and backend API names
const CHAIN_NAME_TO_ID: Record<string, number> = {
  Ethereum: CHAIN_ID_ETHEREUM,

  Sepolia: CHAIN_ID_SEPOLIA,
  'Ethereum Sepolia': CHAIN_ID_SEPOLIA,

  'Z Chain': CHAIN_ID_ZCHAIN,
  'Z-Chain': CHAIN_ID_ZCHAIN,

  Zephyr: CHAIN_ID_ZEPHYR,
  'Z-Chain Zephyr': CHAIN_ID_ZEPHYR,
};

export const RPC_URLS: Record<number, string> = {
  [CHAIN_ID_ETHEREUM]: 'https://rpc.eu-central-1.gateway.fm/v4/ethereum/non-archival/mainnet',
  [CHAIN_ID_SEPOLIA]: 'https://rpc.eu-central-1.gateway.fm/v4/ethereum/non-archival/sepolia',
  [CHAIN_ID_ZCHAIN]: 'https://rpc.zchain.org',
  [CHAIN_ID_ZEPHYR]: 'https://zephyr-rpc.eu-north-2.gateway.fm/',
};

export function getRpcUrl(chainId: number): string | undefined {
  return RPC_URLS[chainId];
}

export function formatBridgeAmount(amount: string, decimals: number = 18): string {
  try {
    return utils.formatUnits(amount, decimals);
  } catch {
    return amount;
  }
}

export interface BridgeTokenInfo {
  symbol: string;
  name: string;
  decimals: number;
  logo?: string;
}

export function getTokenInfo(tokenAddress: string, chainId: number): BridgeTokenInfo {
  const curatedTokens = CURATED_TOKENS[chainId] || [];
  const found = curatedTokens.find((t) => t.tokenAddress.toLowerCase() === tokenAddress.toLowerCase());

  if (found) {
    return {
      symbol: found.symbol,
      name: found.name,
      decimals: found.decimals,
      logo: found.logo,
    };
  }

  return {
    symbol: 'Unknown',
    name: 'Unknown Token',
    decimals: 18,
  };
}

export function getChainIdFromName(chainName: string): number {
  // Try exact match first
  if (CHAIN_NAME_TO_ID[chainName]) {
    return CHAIN_NAME_TO_ID[chainName];
  }
  // Try trimmed and case-insensitive match
  const normalized = chainName.trim();
  if (CHAIN_NAME_TO_ID[normalized]) {
    return CHAIN_NAME_TO_ID[normalized];
  }
  // Try case-insensitive lookup
  const lowerName = normalized.toLowerCase();
  for (const [key, value] of Object.entries(CHAIN_NAME_TO_ID)) {
    if (key.toLowerCase() === lowerName) {
      return value;
    }
  }
  return 0;
}

export function getBridgeStatusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'processing':
      return 'Processing';
    case 'on-hold':
      return 'Ready to Finalize';
    case 'pending':
      return 'Pending';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export interface BridgeActivityData {
  fromChain: string;
  toChain: string;
  amount: string;
  token: string;
  tokenAddress: string;
  destinationAddress: string;
}

export function mapActivityToBridgeParams(
  activity: BridgeActivityData,
  zeroWalletAddress: string | undefined
): BridgeParams {
  const fromChainId = getChainIdFromName(activity.fromChain);
  const toChainId = getChainIdFromName(activity.toChain);

  // Use standard 18 decimals (native tokens and most ERC20s)
  const decimals = 18;
  const formattedAmount = formatBridgeAmount(activity.amount, decimals);

  return {
    fromChainId,
    toChainId,
    amount: formattedAmount,
    fromWalletAddress: zeroWalletAddress,
    toWalletAddress: activity.destinationAddress,
    fromToken: {
      tokenAddress: activity.tokenAddress,
      symbol: '',
      name: '',
      decimals,
    },
    toToken: {
      tokenAddress: activity.tokenAddress,
      symbol: '',
      name: '',
      decimals,
    },
  };
}

export function formatAmount(amount: string): string {
  if (!amount || amount === '0') {
    return '0.0';
  }

  let formatted = amount.replace(/\.?0+$/, '');

  if (!formatted || formatted === '.') {
    return '0.0';
  }

  if (formatted.endsWith('.')) {
    formatted = formatted.slice(0, -1);
  }

  if (!formatted || formatted === '0') {
    return '0.0';
  }

  return formatted;
}

export function getDestinationChainId(fromChainId: number): number {
  if (fromChainId === CHAIN_ID_ETHEREUM) {
    return CHAIN_ID_ZCHAIN; // Ethereum -> ZChain
  } else if (fromChainId === CHAIN_ID_ZCHAIN) {
    return CHAIN_ID_ETHEREUM; // ZChain -> Ethereum
  } else if (fromChainId === CHAIN_ID_SEPOLIA) {
    return CHAIN_ID_ZEPHYR; // Sepolia -> Zephyr
  } else if (fromChainId === CHAIN_ID_ZEPHYR) {
    return CHAIN_ID_SEPOLIA; // Zephyr -> Sepolia
  }
  throw new Error(`Unsupported chain ID: ${fromChainId}`);
}

export function getOriginNetworkId(fromChainId: number): number {
  if (fromChainId === CHAIN_ID_ETHEREUM) {
    return 0; // Ethereum
  } else if (fromChainId === CHAIN_ID_ZCHAIN) {
    return 1; // ZChain
  } else if (fromChainId === CHAIN_ID_SEPOLIA) {
    return 0; // Sepolia
  } else if (fromChainId === CHAIN_ID_ZEPHYR) {
    return 1; // Zephyr
  }
  throw new Error(`Unsupported chain ID: ${fromChainId}`);
}

export const SUPPORTED_L1_CHAINS = [CHAIN_ID_ETHEREUM, CHAIN_ID_SEPOLIA] as const;

export function isSupportedBridgeChain(chainId: number | undefined): boolean {
  if (!chainId) return false;
  return SUPPORTED_L1_CHAINS.includes(chainId as any);
}

export function getL2ChainForL1(l1ChainId: number): number {
  if (l1ChainId === CHAIN_ID_ETHEREUM) {
    return CHAIN_ID_ZCHAIN;
  } else if (l1ChainId === CHAIN_ID_SEPOLIA) {
    return CHAIN_ID_ZEPHYR;
  }
  throw new Error(`Unsupported L1 chain ID: ${l1ChainId}`);
}

export function getL1ChainForL2(l2ChainId: number): number {
  if (l2ChainId === CHAIN_ID_ZCHAIN) {
    return CHAIN_ID_ETHEREUM;
  } else if (l2ChainId === CHAIN_ID_ZEPHYR) {
    return CHAIN_ID_SEPOLIA;
  }
  throw new Error(`Unsupported L2 chain ID: ${l2ChainId}`);
}

export interface AvailableChain {
  id: string;
  name: string;
  chainId: number;
}

export function getAvailableChainsForBridge(connectedChainId: number | undefined): AvailableChain[] {
  if (!connectedChainId) return [];

  // If connected to mainnet, show Ethereum and ZChain
  if (connectedChainId === CHAIN_ID_ETHEREUM) {
    return [
      { id: 'ethereum', name: CHAIN_NAMES[CHAIN_ID_ETHEREUM], chainId: CHAIN_ID_ETHEREUM },
      { id: 'zchain', name: CHAIN_NAMES[CHAIN_ID_ZCHAIN], chainId: CHAIN_ID_ZCHAIN },
    ];
  }
  // If connected to Sepolia, show Sepolia and Zephyr
  if (connectedChainId === CHAIN_ID_SEPOLIA) {
    return [
      { id: 'sepolia', name: CHAIN_NAMES[CHAIN_ID_SEPOLIA], chainId: CHAIN_ID_SEPOLIA },
      { id: 'zephyr', name: CHAIN_NAMES[CHAIN_ID_ZEPHYR], chainId: CHAIN_ID_ZEPHYR },
    ];
  }
  // If connected to L2, show both chains in that pair
  if (connectedChainId === CHAIN_ID_ZCHAIN) {
    return [
      { id: 'ethereum', name: CHAIN_NAMES[CHAIN_ID_ETHEREUM], chainId: CHAIN_ID_ETHEREUM },
      { id: 'zchain', name: CHAIN_NAMES[CHAIN_ID_ZCHAIN], chainId: CHAIN_ID_ZCHAIN },
    ];
  }
  if (connectedChainId === CHAIN_ID_ZEPHYR) {
    return [
      { id: 'sepolia', name: CHAIN_NAMES[CHAIN_ID_SEPOLIA], chainId: CHAIN_ID_SEPOLIA },
      { id: 'zephyr', name: CHAIN_NAMES[CHAIN_ID_ZEPHYR], chainId: CHAIN_ID_ZEPHYR },
    ];
  }
  // Default fallback (shouldn't happen if chain validation is working)
  return [
    { id: 'ethereum', name: CHAIN_NAMES[CHAIN_ID_ETHEREUM], chainId: CHAIN_ID_ETHEREUM },
    { id: 'zchain', name: CHAIN_NAMES[CHAIN_ID_ZCHAIN], chainId: CHAIN_ID_ZCHAIN },
  ];
}

export function formatAddress(address: string | undefined): string {
  if (!address || address === ZERO_ADDRESS) {
    return '0x0000...0000';
  }
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function getExplorerAddressUrl(address: string, chainId: number): string {
  const explorerUrls: Record<number, string> = {
    [CHAIN_ID_ETHEREUM]: 'https://etherscan.io/address/',
    [CHAIN_ID_SEPOLIA]: 'https://sepolia.etherscan.io/address/',
    [CHAIN_ID_ZCHAIN]: 'https://zscan.live/address/',
    [CHAIN_ID_ZEPHYR]: 'https://zephyr-blockscout.eu-north-2.gateway.fm/address/',
  };
  const baseUrl = explorerUrls[chainId] || '';
  return baseUrl ? `${baseUrl}${address}` : '';
}

export function openExplorerForAddress(address: string | undefined, chainId: number | undefined): boolean {
  if (!address || !chainId || address === ZERO_ADDRESS) {
    return false;
  }
  const url = getExplorerAddressUrl(address, chainId);
  if (url) {
    window.open(url, '_blank');
    return true;
  }
  return false;
}

export function getExplorerTransactionUrl(transactionHash: string, chainId: number): string {
  const explorerUrls: Record<number, string> = {
    [CHAIN_ID_ETHEREUM]: 'https://etherscan.io/tx/',
    [CHAIN_ID_SEPOLIA]: 'https://sepolia.etherscan.io/tx/',
    [CHAIN_ID_ZCHAIN]: 'https://zscan.live/tx/',
    [CHAIN_ID_ZEPHYR]: 'https://zephyr-blockscout.eu-north-2.gateway.fm/tx/',
  };
  const baseUrl = explorerUrls[chainId] || '';
  return baseUrl ? `${baseUrl}${transactionHash}` : '';
}

export function openExplorerForTransaction(
  transactionHash: string | undefined,
  chainId: number | undefined,
  explorerUrl?: string | null
): boolean {
  // If API provides a full explorer URL, use it directly
  if (explorerUrl) {
    window.open(explorerUrl, '_blank');
    return true;
  }

  if (!transactionHash || !chainId) {
    return false;
  }
  const url = getExplorerTransactionUrl(transactionHash, chainId);
  if (url) {
    window.open(url, '_blank');
    return true;
  }
  return false;
}

export function getWalletAddressForChain(
  chainId: number | undefined,
  eoaAddress: string | undefined,
  zeroWalletAddress: string | undefined
): string | undefined {
  if (!chainId) return undefined;
  if (L1_CHAIN_IDS.includes(chainId as any)) {
    return eoaAddress;
  }
  if (L2_CHAIN_IDS.includes(chainId as any)) {
    return zeroWalletAddress;
  }
  return eoaAddress; // Default to EOA
}

export function isL1Chain(chainId: number): boolean {
  return L1_CHAIN_IDS.includes(chainId as any);
}

export function isL2Chain(chainId: number): boolean {
  return L2_CHAIN_IDS.includes(chainId as any);
}

export interface TokenInfo {
  chainId: number;
  tokenAddress?: string;
}

export interface L1L2TokenInfo {
  l1Address?: string;
  l2Address?: string;
  l1ChainId: number;
  l2ChainId: number;
}

export function getL1L2TokenInfo(fromToken: TokenInfo | null, toToken: TokenInfo | null): L1L2TokenInfo | null {
  if (!fromToken || !toToken) return null;

  const fromIsL1 = isL1Chain(fromToken.chainId);
  const fromIsL2 = isL2Chain(fromToken.chainId);

  if (fromIsL1) {
    return {
      l1Address: fromToken.tokenAddress,
      l2Address: toToken.tokenAddress,
      l1ChainId: fromToken.chainId,
      l2ChainId: toToken.chainId,
    };
  }

  if (fromIsL2) {
    return {
      l1Address: toToken.tokenAddress,
      l2Address: fromToken.tokenAddress,
      l1ChainId: toToken.chainId,
      l2ChainId: fromToken.chainId,
    };
  }

  // Fallback: assume fromToken is L1 if neither check passes
  return {
    l1Address: fromToken.tokenAddress,
    l2Address: toToken.tokenAddress,
    l1ChainId: fromToken.chainId,
    l2ChainId: toToken.chainId,
  };
}

export type BridgeValidationError =
  | 'NO_FROM_TOKEN'
  | 'NO_TO_TOKEN'
  | 'SAME_CHAIN'
  | 'INVALID_AMOUNT'
  | 'NO_TOKEN_ADDRESS'
  | 'INSUFFICIENT_BALANCE'
  | null;

export function getBridgeValidationError(
  fromChainId: number | null | undefined,
  toChainId: number | null | undefined,
  amount: string | null | undefined,
  tokenAddress: string | null | undefined,
  tokenBalance: string | null | undefined
): BridgeValidationError {
  if (!fromChainId) return 'NO_FROM_TOKEN';
  if (!toChainId) return 'NO_TO_TOKEN';
  if (fromChainId === toChainId) return 'SAME_CHAIN';
  if (!tokenAddress) return 'NO_TOKEN_ADDRESS';

  if (!amount || Number(amount) <= 0) return 'INVALID_AMOUNT';

  // Check if user has balance for the token
  const balance = tokenBalance ? Number(tokenBalance) : 0;
  if (balance <= 0) return 'INSUFFICIENT_BALANCE';

  // Check if amount exceeds balance
  const amountNum = Number(amount);
  if (amountNum > balance) return 'INSUFFICIENT_BALANCE';

  return null;
}

export function getBridgeValidationErrorMessage(error: BridgeValidationError): string {
  switch (error) {
    case 'NO_FROM_TOKEN':
      return 'Please select a token to bridge';
    case 'NO_TO_TOKEN':
      return 'Error fetching bridge counterpart token information';
    case 'SAME_CHAIN':
      return 'Cannot bridge to the same chain';
    case 'INVALID_AMOUNT':
      return 'Please enter a valid amount greater than 0';
    case 'NO_TOKEN_ADDRESS':
      return 'Token address is required';
    case 'INSUFFICIENT_BALANCE':
      return 'Insufficient balance for this transaction';
    default:
      return '';
  }
}

/**
 * Gets an ethers Web3Provider from the connected Wagmi wallet.
 * Works in both browser and Electron environments.
 *
 * @param chainId - The chain ID to get the provider for
 * @returns An ethers Web3Provider instance
 * @throws Error if wallet is not connected or provider cannot be obtained
 */
export async function getEthersProviderFromWagmi(chainId: number): Promise<providers.Web3Provider> {
  const wagmiConfig = getWagmiConfig();
  const account = getAccount(wagmiConfig);

  if (!account.isConnected || !account.address) {
    throw new Error('No wallet detected. Please connect your wallet.');
  }

  const connector = account.connector;
  if (!connector) {
    throw new Error('Wallet connector not found.');
  }

  const isInjectedConnector = (() => {
    const id = (connector as any)?.id;
    const name = String((connector as any)?.name ?? '').toLowerCase();
    // RainbowKit/Wagmi commonly uses id/name like "injected" for MetaMask/Brave/etc.
    return id === 'injected' || name.includes('injected') || name.includes('metamask');
  })();

  // Try multiple methods to get the provider for compatibility
  // IMPORTANT: Don't pass chainId to getProvider() initially as it triggers chain switching
  // which fails in Electron/WalletConnect. Get provider first, then check/switch chain if needed.
  let provider: any;
  // Prefer the connector's provider unless we're explicitly using an injected connector.
  // In Electron it’s possible to have a `window.ethereum` shim/extension present that has *no accounts*,
  // which causes ethers v5 to throw `unknown account #0` when calling getSigner().getAddress().
  if (isInjectedConnector && typeof window !== 'undefined' && (window as any).ethereum) {
    provider = (window as any).ethereum;
  } else if (typeof connector.getProvider === 'function') {
    // Try to get provider without chainId first to avoid triggering chain switch
    try {
      provider = await connector.getProvider();
    } catch (error) {
      // If getProvider() without args fails, try accessing provider directly
      // This avoids WalletConnect chain switching issues in Electron
      if ((connector as any).provider) {
        provider = (connector as any).provider;
      } else if ((connector as any).transport?.value?.provider) {
        provider = (connector as any).transport.value.provider;
      } else if ((connector as any).transport?.provider) {
        provider = (connector as any).transport.provider;
      } else {
        // Last resort: try with chainId (may trigger chain switch)
        provider = await connector.getProvider({ chainId });
      }
    }
  } else if ((connector as any).provider) {
    provider = (connector as any).provider;
  } else if ((connector as any).transport?.value?.provider) {
    provider = (connector as any).transport.value.provider;
  } else if ((connector as any).transport?.provider) {
    provider = (connector as any).transport.provider;
  }

  if (!provider) {
    throw new Error('Unable to get wallet provider. Please ensure your wallet is connected and try again.');
  }

  // Use "any" to avoid ethers caching the network and failing after a chain switch.
  const ethersProvider = new providers.Web3Provider(provider, 'any');
  const network = await ethersProvider.getNetwork();

  // Only switch chain if user is not on the correct chain
  if (network.chainId !== chainId) {
    // Check if connector supports switchChain
    if (typeof connector.switchChain === 'function') {
      try {
        await connector.switchChain({ chainId });
        // After switching, get the network again to verify
        const newNetwork = await ethersProvider.getNetwork();
        if (newNetwork.chainId !== chainId) {
          throw new Error(
            `Please switch to the correct network. Expected chain ID ${chainId}, but connected to ${newNetwork.chainId}`
          );
        }
      } catch (switchError: any) {
        // If switchChain fails, provide a helpful error message
        if (
          switchError?.message?.includes('not configured') ||
          switchError?.message?.includes('Chain not configured')
        ) {
          throw new Error(
            `Chain ${chainId} is not configured in your wallet. Please add it manually or switch to chain ${chainId} in your wallet app.`
          );
        }
        throw new Error(
          `Please switch to the correct network. Expected chain ID ${chainId}, but connected to ${
            network.chainId
          }. Error: ${switchError?.message || switchError}`
        );
      }
    } else {
      // Connector doesn't support switchChain, user needs to switch manually
      throw new Error(
        `Please switch to the correct network. Expected chain ID ${chainId}, but connected to ${network.chainId}`
      );
    }
  }

  // Ensure we actually have wallet accounts accessible to ethers.
  // This is critical in Electron + WalletConnect where wagmi can be "connected" (rehydrated)
  // but `eth_accounts` returns [] until an explicit request is made.
  const listAccountsSafe = async (): Promise<string[]> => {
    try {
      return await ethersProvider.listAccounts();
    } catch {
      return [];
    }
  };

  let accounts = await listAccountsSafe();
  if (!accounts.length) {
    try {
      // Trigger wallet to expose accounts (no-op if already authorized)
      await ethersProvider.send('eth_requestAccounts', []);
    } catch {
      // ignore; we’ll validate accounts below and raise a user-friendly error
    }
    accounts = await listAccountsSafe();
  }

  if (!accounts.length) {
    throw new Error('Wallet is connected, but no accounts are available. Please reconnect your wallet and try again.');
  }

  return ethersProvider;
}

export function normalizeWalletError(error: unknown): Error {
  const safeStringify = (value: unknown): string => {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const extractMessage = (err: unknown): string => {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (typeof err === 'object') {
      const e: any = err;
      return e?.shortMessage || e?.reason || e?.message || e?.error?.message || e?.data?.message || safeStringify(err);
    }
    return String(err);
  };

  const errorMessage = extractMessage(error);
  const errorCode = (error as any)?.code;
  const errorReason = (error as any)?.reason;

  if (
    errorMessage.includes('execution reverted') ||
    errorMessage.includes('transaction may fail') ||
    errorMessage.includes('transaction failed') ||
    errorMessage.includes('cannot estimate gas') ||
    errorMessage.includes('UNPREDICTABLE_GAS_LIMIT') ||
    errorMessage.includes('CALL_EXCEPTION') ||
    errorCode === 3 || // Execution reverted
    errorCode === 'CALL_EXCEPTION' ||
    errorReason === 'execution reverted'
  ) {
    return new Error('Transaction failed. Please check your wallet and try again.');
  }

  if (errorMessage.includes('user rejected') || errorMessage.includes('User denied') || errorCode === 4001) {
    return new Error('Transaction was cancelled.');
  }

  if (errorMessage.includes('insufficient funds') || errorMessage.includes('insufficient balance')) {
    return new Error('Insufficient funds for this transaction.');
  }

  if (errorMessage.includes('network') || errorMessage.includes('connection') || errorCode === 'NETWORK_ERROR') {
    return new Error('Network error. Please check your connection and try again.');
  }

  if (errorMessage.includes('wallet') || errorMessage.includes('MetaMask') || errorMessage.includes('RPC')) {
    return new Error('Wallet error. Please try again.');
  }

  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Merges two sorted lists of bridge activities by interleaving based on relative recency.
 * Since globalIndex values are in different ranges per chain, we can't directly compare them.
 * Instead, we use a merge algorithm that compares items based on their relative position
 * in each sorted list. Since the API returns items sorted by recency, position is a reliable indicator.
 *
 * @param l1Deposits - Sorted list of bridge activities from L1 chain (most recent first)
 * @param l2Deposits - Sorted list of bridge activities from L2 chain (most recent first)
 * @returns Merged and interleaved list of activities sorted by relative recency
 */
export function mergeBridgeActivities<T>(l1Deposits: T[], l2Deposits: T[]): T[] {
  const merged: T[] = [];
  let l1Index = 0;
  let l2Index = 0;

  // Helper to calculate a "recency score" for an item based on its position
  // Items earlier in the list (lower index) are more recent
  // Position is the primary indicator since the API returns items sorted by recency
  const getRecencyScore = (position: number, listLength: number): number => {
    if (listLength <= 1) return 1;
    const normalizedPosition = position / (listLength - 1);
    return 1 - normalizedPosition;
  };

  // Merge the two sorted lists
  while (l1Index < l1Deposits.length || l2Index < l2Deposits.length) {
    if (l1Index >= l1Deposits.length) {
      merged.push(...l2Deposits.slice(l2Index));
      break;
    }
    if (l2Index >= l2Deposits.length) {
      merged.push(...l1Deposits.slice(l1Index));
      break;
    }

    // Compare recency scores based on position in each sorted list
    // Since both lists are sorted by recency, position is the best indicator
    const l1Score = getRecencyScore(l1Index, l1Deposits.length);
    const l2Score = getRecencyScore(l2Index, l2Deposits.length);

    if (l1Score >= l2Score) {
      merged.push(l1Deposits[l1Index]);
      l1Index++;
    } else {
      merged.push(l2Deposits[l2Index]);
      l2Index++;
    }
  }

  return merged;
}
